import { useState, useRef, useEffect } from 'react'
import { Bell, User, Menu, X, AlertTriangle, Info, Calendar, LogOut } from 'lucide-react'
import { useNavigate, useRouterState } from '@tanstack/react-router'

import { useDashboardStore } from '@/core/store/dashboardStore'
import { useAuthStore } from '@/core/store/authStore'
import { useIsMounted } from '@/core/hooks/useIsMounted'
import { useClassSessionStore, getCertificateStatus } from '@/features/classes/stores'
import { useCRMStore } from '@/features/crm/stores'
import { useInboxStore } from '@/features/emails/stores/inboxStore'
import { cn } from '@/core/lib/utils'
import { fmtDate, DATE_FMT_SHORT } from '@/core/lib/formatters'

const pageTitles: Record<string, string> = {
  overview: 'Dashboard',
  classes:  'Courses & Classes',
  courses:  'Online Courses',
  crm:      'CRM',
  emails:   'Emails',
  reports:  'Reports',
  settings: 'Settings',
  ai:       'AI Hub',
  approvals: 'AI Approvals',
  agents:    'Agent Settings',
}

type NotifType = 'warning' | 'info' | 'default'

interface Notification {
  id: string
  type: NotifType
  text: string
  sub: string
  link?: string
}

function NotifIcon({ type }: { type: NotifType }) {
  if (type === 'warning') {return <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />}
  if (type === 'info')    {return <Calendar className="w-4 h-4 text-blue-500 shrink-0" />}
  return <Info className="w-4 h-4 text-slate-500 shrink-0" />
}

export function Header() {
  const mounted = useIsMounted()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const sidebarOpen = useDashboardStore((s) => s.sidebarOpen)
  const toggleSidebar = useDashboardStore((s) => s.toggleSidebar)

  // Derive page title from current pathname
  const segment = pathname.split('/')[1] ?? ''
  const pageTitle = pageTitles[segment] ?? 'Dashboard'

  const authUser = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  function handleLogout() {
    logout()
    void navigate({ to: '/', replace: true })
  }

  const { certificates, sessions } = useClassSessionStore()
  const { contacts } = useCRMStore()
  const { unreadCount: emailUnreadCount, refreshUnreadCount } = useInboxStore()

  // Load email unread count on mount
  useEffect(() => {
    void (async () => {
      await refreshUnreadCount()
      if (!mounted.current) {
        return
      }
    })()
  }, [refreshUnreadCount, mounted])

  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Build notifications
  const now = new Date()
  const in90Days = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)

  const notifications: Notification[] = [
    // Certificates expiring within 90 days
    ...certificates
      .filter((c) => {
        const exp = new Date(c.expiresAt)
        return getCertificateStatus(c) === 'valid' && exp <= in90Days
      })
      .map((c) => {
        const name = c.attendeeDetail
          ? `${c.attendeeDetail.firstName} ${c.attendeeDetail.lastName}`.trim()
          : ''
        return {
          id: `cert-${c.id}`,
          type: 'warning' as NotifType,
          text: name ? `Certificate expiring: ${name}` : `Certificate ${c.certificateNumber} expiring soon`,
          sub: `${c.certificateNumber} · expires ${fmtDate(c.expiresAt, DATE_FMT_SHORT)}`,
          link: '/classes',
        }
      }),

    // Active sessions
    ...sessions
      .filter((s) => s.status === 'active')
      .map((s) => ({
        id: `sess-${s.id}`,
        type: 'info' as NotifType,
        text: s.courseName ? `${s.courseName} — class in progress` : `Class in progress: ${s.instructorName}`,
        sub: [
          s.locationName || s.location,
          s.endTime && `ends ${s.endTime}`,
          typeof s.attendeeCount === 'number' && `${s.attendeeCount} attendee${s.attendeeCount === 1 ? '' : 's'}`,
        ].filter(Boolean).join(' · '),
        link: '/classes',
      })),

    // Upcoming sessions (draft, within next 7 days)
    ...sessions
      .filter((s) => {
        if (s.status !== 'draft') {return false}
        const diff = (new Date(s.date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        return diff >= 0 && diff <= 7
      })
      .map((s) => ({
        id: `upcoming-${s.id}`,
        type: 'info' as NotifType,
        text: s.courseName ? `${s.courseName} · ${fmtDate(s.date, DATE_FMT_SHORT)}` : `Upcoming session: ${s.instructorName}`,
        sub: [
          s.locationName || s.location,
          s.startTime && `starts ${s.startTime}`,
          s.instructorName && `instructor: ${s.instructorName}`,
        ].filter(Boolean).join(' · '),
        link: '/classes',
      })),

    // CRM contacts pending proposal
    ...contacts
      .filter((c) => c.stage === 'proposal' && c.status !== 'inactive')
      .map((c) => {
        const name = `${c.firstName} ${c.lastName}`.trim()
        return {
          id: `crm-${c.id}`,
          type: 'default' as NotifType,
          text: `Pending proposal: ${c.company || name}`,
          sub: [
            name && `Contact: ${name}`,
            c.lastContact && `last contact: ${fmtDate(c.lastContact, DATE_FMT_SHORT)}`,
          ].filter(Boolean).join(' · '),
          link: '/crm',
        }
      }),

    // Unread inbound emails
    ...(emailUnreadCount > 0
      ? [{
          id: 'inbox-unread',
          type: 'info' as NotifType,
          text: `${emailUnreadCount} unread email${emailUnreadCount > 1 ? 's' : ''}`,
          sub: 'Open the Emails page to review them',
          link: '/emails',
        }]
      : []),
  ]

  const hasNotifs = notifications.length > 0

  return (
    <header
      className={cn(
        'fixed top-0 right-0 left-0 h-16 bg-white border-b border-slate-150 flex items-center justify-between px-4 md:px-6 z-10 transition-all duration-300',
        sidebarOpen ? 'lg:left-64' : 'lg:left-16'
      )}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg hover:bg-gray-50 text-slate-500 transition-colors"
          aria-label="Toggle sidebar"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <h1 className="text-base md:text-lg font-semibold text-navy">
          {pageTitle}
        </h1>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Notifications bell */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen((o) => !o)}
            className="relative p-2 rounded-lg hover:bg-gray-50 text-slate-500 transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {hasNotifs && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
            )}
          </button>

          {/* Dropdown */}
          {notifOpen && (
            <div className="fixed top-16 left-3 right-3 z-50 sm:absolute sm:top-full sm:left-auto sm:right-0 sm:w-80 sm:mt-2 bg-white border border-slate-150 rounded-xl shadow-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-150">
                <p className="text-sm font-semibold text-navy">Notifications</p>
                {hasNotifs && (
                  <span className="text-xs bg-primary text-white rounded-full px-1.5 py-0.5">
                    {notifications.length}
                  </span>
                )}
              </div>

              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">No notifications</p>
                ) : (
                  notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        setNotifOpen(false)
                        if (n.link) {void navigate({ to: n.link })}
                      }}
                      className="w-full flex items-start gap-3 px-4 py-3 border-b border-slate-150 last:border-0 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="mt-0.5">
                        <NotifIcon type={n.type} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-navy font-medium truncate">{n.text}</p>
                        <p className="text-xs text-slate-500">{n.sub}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pl-3 border-l border-slate-150">
          <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-white" />
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-navy">{authUser?.name || 'Admin'}</p>
            <p className="text-xs text-slate-500">{authUser?.email || 'Safety Manager'}</p>
          </div>
          <button
            onClick={handleLogout}
            className="ml-1 p-1.5 rounded-lg hover:bg-gray-50 text-slate-500 hover:text-red-500 transition-colors"
            aria-label="Log out"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  )
}
